import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { format, startOfDay, endOfDay, isValid } from 'date-fns';
import debounce from 'lodash/debounce';

import useViewer from 'hooks/useViewer';
import { useApiGateway } from 'hooks/useApi';
import { calendarTabs } from 'constants/calendarTabs';
import { CalIcon } from '../../../svg/CalendarIcon';
import { FilterPanel } from '../components/FilterPanel';
import TableGrid from 'components/TableGrid';
import { SectionHeader } from './SectionHeader';
import { EmptyState } from './EmptyState';
import { useCalendarData } from '../hooks/useCalendarData';
import { useWatchlist } from '../hooks/useWatchlist';
import { formatDateToString, BaseCalendarPageProps } from 'screens/Calendar';
import { convertToCSV, downloadCSV } from 'utils/client/csvUtils';
import { AccessModal } from './AccessModal';
import { CALENDAR_DATA_BY_DATE } from 'constants/stockApi';
import { getCalendarApiSlug } from 'screens/Calendar/hooks/useNativeCalendarData';

interface BaseCalendarProps extends BaseCalendarPageProps {
  screenType: string;
  title: string;
  columns: any;
  columnWidths: number[];
  mobileCard: React.ComponentType<any>;
  filterData: (data: any[], filters: any) => any[];
  transformData?: (data: any[]) => any[];
  showWatchlistToggle?: boolean;
  symbol?: string;
}

export const BaseCalendar: React.FC<BaseCalendarProps> = ({
  symbol,
  data,
  dynamicData = [],
  setDynamicData,
  isLoadingDynamic,
  setIsLoadingDynamic,
  slug,
  screenType,
  title,
  columns,
  columnWidths,
  mobileCard: MobileCard,
  filterData,
  transformData,
  showWatchlistToggle = true,
}) => {
  const viewer = useViewer();
  const hasAccess = viewer?.viewer?.uid;
  const currentRequestRef = useRef<string | null>(null);

  const activeTab = calendarTabs.find((tab) => tab.slug === slug);
  const calendarData = useCalendarData();
  const { bookmarkedItems, handleBookmark: originalHandleBookmark } = useWatchlist();

  const [selectedThirdSectionFilters, setSelectedThirdSectionFilters] = useState<Set<string>>(
    new Set(['all']),
  );
  const [selectedFourthSectionFilters, setSelectedFourthSectionFilters] = useState<Set<string>>(
    new Set(['all']),
  );

  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [accessModalTitle, setAccessModalTitle] = useState('');

  const handlePremiumFeature = (title: string) => {
    if (!hasAccess) {
      setAccessModalTitle(title);
      setIsDownloadModalOpen(true);
      return true;
    }
    return false;
  };

  const handleBookmark = (symbol: string) => {
    if (handlePremiumFeature('Log in to watch list')) {
      return;
    }

    originalHandleBookmark(symbol);
  };

  const handleDownload = (): void => {
    if (handlePremiumFeature('Log in to download data')) {
      return;
    }

    const csvData = processedData.map((item: any) => {
      const obj: any = {};

      const visibleColumns = columns(
        bookmarkedItems,
        handleBookmark,
        calendarData.tickerData,
        slug,
      );

      visibleColumns.forEach((column: any) => {
        const possibleKeys = [
          column.sortKey,
          column.accessorKey,
          column.key,
          column.id,
          column.accessor,
          column.dataKey,
        ].filter(Boolean);

        const headerName = column.name || column.header || column.title || possibleKeys[0];

        let value = undefined;

        if (column.accessorFn && typeof column.accessorFn === 'function') {
          try {
            value = column.accessorFn(item);
          } catch (error) {
            console.warn(`Error executing accessorFn for column ${headerName}:`, error);
          }
        }

        if (value === undefined) {
          for (const key of possibleKeys) {
            if (key && item.hasOwnProperty(key)) {
              value = item[key];
              break;
            }
          }
        }

        if (value === undefined && possibleKeys.length > 0) {
          for (const key of possibleKeys) {
            if (key && key.includes('.')) {
              const keyParts = key.split('.');
              let nestedValue = item;

              for (const part of keyParts) {
                if (nestedValue && nestedValue.hasOwnProperty(part)) {
                  nestedValue = nestedValue[part];
                } else {
                  nestedValue = undefined;
                  break;
                }
              }

              if (nestedValue !== undefined) {
                value = nestedValue;
                break;
              }
            }
          }
        }

        if (value === undefined && column.cell && typeof column.cell === 'function') {
          try {
            const cellResult = column.cell({
              getValue: () =>
                column.accessorFn ? column.accessorFn(item) : item[possibleKeys[0]] || '',
              row: { original: item },
            });
            if (cellResult && typeof cellResult === 'object' && cellResult.props) {
              value = cellResult.props.children || cellResult.props.value || '';
            } else if (typeof cellResult === 'string' || typeof cellResult === 'number') {
              value = cellResult;
            }
          } catch (error) {
            console.warn(
              `Error extracting value from cell renderer for column ${headerName}:`,
              error,
            );
          }
        }

        if (value !== undefined && value !== null) {
          if (column.csvType === 'date' && value) {
            if (value instanceof Date) {
              value = value.toISOString().split('T')[0];
            } else if (typeof value === 'string' || typeof value === 'number') {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                value = date.toISOString().split('T')[0];
              }
            }
          } else if (column.csvType === 'currency' && typeof value === 'number') {
            value = `$${value.toLocaleString()}`;
          } else if (column.csvType === 'currency-large' && typeof value === 'number') {
            value = `$${value.toLocaleString()}`;
          } else if (column.csvType === 'eps' && typeof value === 'number') {
            value = `$${value.toFixed(2)}`;
          } else if (column.csvType === 'percentage' && typeof value === 'number') {
            value = `${(value * 100).toFixed(2)}%`;
          } else if (column.csvType === 'percentage-formatted' && typeof value === 'number') {
            value = `${Math.round(value)}%`;
          } else if (column.csvType === 'number' && typeof value === 'number') {
            value = value.toLocaleString();
          }

          obj[headerName] = value;
        } else {
          console.warn(`Column "${headerName}" not found in data. Tried keys:`, possibleKeys);
          obj[headerName] = '';
        }
      });

      return obj;
    });

    const currentDate = new Date().toISOString().split('T')[0];
    let filename = `${title.replace(/\s+/g, '_')}_${currentDate}`;

    if (calendarData.selectedDate) {
      filename += `_${formatDateToString(calendarData.selectedDate)}`;
    }
    if (calendarData.isWatchListOnly) {
      filename += '_watchlist';
    }

    filename += '.csv';

    const csv = convertToCSV(csvData);
    downloadCSV(csv, filename);
  };

  const handleClose = () => {
    setIsDownloadModalOpen(false);
    setAccessModalTitle('');
  };

  // Merge dynamic patches by date into base SSG data so we can progressively
  // refresh individual dates (e.g., Today) without losing the full dataset.
  const combinedData = useMemo(() => {
    if (!dynamicData?.length) return data;
    const byDate = new Map<string, any>();
    (data || []).forEach((d: any) => {
      if (d && d.date) byDate.set(d.date, d);
    });
    dynamicData.forEach((p: any) => {
      if (p && p.date) byDate.set(p.date, p);
    });
    // Preserve base order; append any new dynamic dates
    const baseDates = new Set((data || []).map((d: any) => d?.date).filter(Boolean));
    const merged = (data || []).map((d: any) => (d?.date ? byDate.get(d.date) || d : d));
    dynamicData.forEach((p: any) => {
      if (p?.date && !baseDates.has(p.date)) merged.push(p);
    });
    return merged;
  }, [data, dynamicData]);

  const applySorting = (data: any[], sortingConfig: { id: string; desc: boolean }[]) => {
    if (!sortingConfig || sortingConfig.length === 0) return data;

    return [...data].sort((a, b) => {
      for (const sort of sortingConfig) {
        const aValue = a[sort.id] ?? 0;
        const bValue = b[sort.id] ?? 0;

        if (aValue === bValue) continue;

        const comparison = aValue < bValue ? -1 : 1;
        return sort.desc ? -comparison : comparison;
      }
      return 0;
    });
  };

  const processedData = useMemo(() => {
    const transformed = transformData ? transformData(combinedData) : combinedData;

    if (!transformed || transformed.length === 0) {
      return [];
    }

    let filterOptions;

    if (symbol) {
      filterOptions = {
        selectedDate: null,
        fromDate: null,
        toDate: null,
        selectedTimeframe: null,
        selectedThirdSectionFilters,
        selectedFourthSectionFilters,
        isWatchListOnly: calendarData.isWatchListOnly,
        bookmarkedItems,
      };
    } else {
      filterOptions = {
        selectedDate: calendarData.selectedDate,
        fromDate: calendarData.fromDate,
        toDate: calendarData.toDate,
        selectedTimeframe: calendarData.selectedTimeframe,
        selectedThirdSectionFilters,
        selectedFourthSectionFilters,
        isWatchListOnly: calendarData.isWatchListOnly,
        bookmarkedItems,
      };
    }

    const filtered = filterData(transformed, filterOptions);

    return applySorting(filtered, [{ id: 'marketCap', desc: true }]);
  }, [
    combinedData,
    transformData,
    filterData,
    symbol,
    calendarData.selectedDate,
    calendarData.fromDate,
    calendarData.toDate,
    calendarData.selectedTimeframe,
    selectedThirdSectionFilters,
    selectedFourthSectionFilters,
    calendarData.isWatchListOnly,
    bookmarkedItems,
    slug,
  ]);

  const { get: getfetchDataForDate } = useApiGateway(
    `${CALENDAR_DATA_BY_DATE}/${getCalendarApiSlug(slug)}`,
  );

  const fetchDataForDate = useCallback(
    async (date: string) => {
      if (!setDynamicData || !setIsLoadingDynamic) return;

      if (currentRequestRef.current === date) {
        return;
      }

      currentRequestRef.current = date;
      setIsLoadingDynamic(true);

      try {
        const result = await getfetchDataForDate({
          endpoint: `/${date}`,
        });

        if (result.isError) {
          throw new Error(result.error || 'Failed to fetch data');
        }

        const dateData = result.data;
        const formattedData = [
          {
            date: date,
            items: dateData.items || dateData || [],
            lastUpdated: dateData.lastUpdated || new Date().toISOString(),
          },
        ];

        // Merge/accumulate dynamic patches by date rather than replacing all
        const prev = Array.isArray(dynamicData) ? dynamicData : [];
        const idx = prev.findIndex((d: any) => d?.date === date);
        let next = prev;
        if (idx >= 0) {
          next = prev.slice();
          next[idx] = formattedData[0];
        } else {
          next = [...prev, ...formattedData];
        }
        setDynamicData(next);
      } catch (error) {
        console.error('Error fetching date-specific data:', error);
        setDynamicData([]);
      } finally {
        setIsLoadingDynamic(false);
        currentRequestRef.current = null;
      }
    },
    [getfetchDataForDate, setDynamicData, setIsLoadingDynamic, dynamicData],
  );

  const handleDateChangeInternal = useCallback(
    async (event: any) => {
      if (event && event?.length > 0) {
        const selectedDate = event[0];

        if (!isValid(selectedDate)) {
          console.error('Invalid date received:', selectedDate);
          return;
        }

        const dateString = format(selectedDate, 'yyyy-MM-dd');

        const currentDateString = calendarData.selectedDate
          ? format(calendarData.selectedDate, 'yyyy-MM-dd')
          : '';

        if (dateString === currentDateString) {
          return;
        }

        const hasStaticData = data.some((day) => day.date === dateString);
        const hasDynamicPatch = dynamicData?.some((day) => day.date === dateString);
        if (!hasStaticData && !hasDynamicPatch) {
          await fetchDataForDate(dateString);
        }

        const startOfSelectedDay = startOfDay(selectedDate);
        const endOfSelectedDay = endOfDay(selectedDate);

        calendarData.setFromDate(startOfSelectedDay);
        calendarData.setSelectedDate(selectedDate);
        calendarData.setDateValue(format(selectedDate, 'MM/dd/yyyy'));
        calendarData.setToDate(endOfSelectedDay);
        calendarData.setSelectedTimeframe('Today');
      }
    },
    [data, calendarData, setDynamicData, fetchDataForDate],
  );

  const handleDateChange = useMemo(
    () => debounce(handleDateChangeInternal, 300),
    [handleDateChangeInternal],
  );

  const clearDateSelection = () => {
    calendarData.setSelectedDate(null);
    calendarData.setDateValue('');
    calendarData.setFromDate(null);
    calendarData.setToDate(null);

    if (setDynamicData) {
      setDynamicData([]);
    }
  };

  const toggleThirdSectionFilter = (value: string) => {
    setSelectedThirdSectionFilters((prev) => {
      const newFilters = new Set(prev);
      if (value === 'all') {
        newFilters.clear();
        newFilters.add('all');
      } else {
        newFilters.delete('all');
        if (newFilters.has(value)) {
          newFilters.delete(value);
        } else {
          newFilters.add(value);
        }
        if (newFilters.size === 0) {
          newFilters.add('all');
        }
      }
      return newFilters;
    });
  };

  const toggleFourthSectionFilter = (value: string) => {
    setSelectedFourthSectionFilters((prev) => {
      const newFilters = new Set(prev);

      if (value === 'all') {
        newFilters.clear();
        newFilters.add('all');
      } else {
        newFilters.delete('all');

        if (value === 'Buy') {
          const hasBuy = newFilters.has('Strong Buy') || newFilters.has('Buy');
          if (hasBuy) {
            newFilters.delete('Strong Buy');
            newFilters.delete('Buy');
          } else {
            newFilters.add('Strong Buy');
            newFilters.add('Buy');
          }
        } else if (value === 'Sell') {
          const hasSell = newFilters.has('Strong Sell') || newFilters.has('Sell');
          if (hasSell) {
            newFilters.delete('Strong Sell');
            newFilters.delete('Sell');
          } else {
            newFilters.add('Strong Sell');
            newFilters.add('Sell');
          }
        } else {
          if (newFilters.has(value)) {
            newFilters.delete(value);
          } else {
            newFilters.add(value);
          }
        }

        if (newFilters.size === 0) {
          newFilters.add('all');
        }
      }

      return newFilters;
    });
  };

  const defaultSorting = useMemo(() => {
    if (slug === 'economic-events') {
      return [];
    }
    return [{ id: 'marketCap', desc: true }];
  }, [slug]);

  useEffect(() => {
    return () => {
      handleDateChange.cancel();
    };
  }, [handleDateChange]);

  // No background refresh; only fetch on explicit user date selection when needed.

  return (
    <div className="mt-5 flex flex-col p-2">
      <p className="m-auto mb-4 max-w-3xl px-4 text-center text-sm text-gray-300 md:mb-8 md:text-base">
        {activeTab?.description}
      </p>
      {!symbol && (
        <div className="mx-auto w-full rounded-lg border border-brand-green-primary bg-palette-black-4 2xl:w-fit">
          <FilterPanel
            screenType={screenType}
            selectedTicker={calendarData.selectedTicker}
            setSelectedTicker={calendarData.setSelectedTicker}
            tickerData={calendarData.tickerData}
            searchResults={calendarData.searchResults}
            setSearchResults={calendarData.setSearchResults}
            dateValue={calendarData.dateValue}
            selectedDate={calendarData.selectedDate}
            handleDateChange={handleDateChange}
            clearDateSelection={clearDateSelection}
            selectedTimeframe={calendarData.selectedTimeframe}
            setSelectedTimeframe={calendarData.setSelectedTimeframe}
            selectedThirdSectionFilters={selectedThirdSectionFilters}
            toggleThirdSectionFilter={toggleThirdSectionFilter}
            selectedFourthSectionFilters={selectedFourthSectionFilters}
            toggleFourthSectionFilter={toggleFourthSectionFilter}
          />
        </div>
      )}
      <div className="container mx-auto mt-4 max-w-full">
        <div className="hidden md:block">
          <div className="relative">
            <div className="absolute inset-px rounded-lg bg-palette-black-4"></div>
            <div className="relative flex h-full flex-col overflow-hidden">
              <div className="overflow-x-auto">
                <TableGrid<any>
                  showSectionHeader
                  capitalizeHeaders
                  columns={columns(bookmarkedItems, handleBookmark, calendarData.tickerData, slug)}
                  columnWidths={columnWidths}
                  data={processedData || []}
                  loading={processedData.length === 0 && (calendarData.loading || isLoadingDynamic)}
                  pageSize={500}
                  enableSorting={true}
                  isUseColGroup
                  sectionHeader={
                    <SectionHeader
                      title={title}
                      Icon={CalIcon}
                      isWatchListOnly={calendarData.isWatchListOnly}
                      setIsWatchListOnly={calendarData.setIsWatchListOnly}
                      dataLength={processedData.length}
                      showWatchlistToggle={showWatchlistToggle}
                      slug={slug}
                      symbol={symbol}
                      screenType={screenType}
                      handleDownload={handleDownload}
                      handlePremiumFeature={handlePremiumFeature}
                    />
                  }
                  defaultSorting={defaultSorting}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="md:hidden">
          {processedData.length === 0 && (calendarData.loading || isLoadingDynamic) ? (
            <div className="flex justify-center py-8">
              <div className="text-gray-400">Loading...</div>
            </div>
          ) : processedData && processedData.length > 0 ? (
            <div className="space-y-4">
              {processedData.map((item, index) => {
                return (
                  <MobileCard
                    key={`${item.symbol || item.company || item.event}-${index}`}
                    item={item}
                    isBookmarked={bookmarkedItems.has(item.symbol)}
                    onBookmark={handleBookmark}
                    tickerData={calendarData.tickerData}
                    slug={slug}
                  />
                );
              })}
            </div>
          ) : (
            <EmptyState
              selectedDate={calendarData.selectedDate}
              onClearDate={clearDateSelection}
              type={screenType}
            />
          )}
        </div>
      </div>
      <AccessModal
        isOpen={isDownloadModalOpen}
        handleClose={handleClose}
        title={accessModalTitle || 'Access Required'}
      />
    </div>
  );
};
